import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinkClass = (isActive?: boolean) =>
    `text-sm font-medium whitespace-nowrap transition-colors duration-300 hover:text-primary-500 ${
      scrolled ? 'text-foreground-800' : 'text-white/95'
    } ${isActive ? 'text-primary-500' : ''}`;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50 shadow-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10 flex items-center justify-between h-[64px] md:h-[72px]">
        <Link
          to="/"
          className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap transition-colors duration-300 ${
            scrolled ? 'text-foreground-950' : 'text-white'
          }`}
        >
          pandabuy<span className="text-primary-500">.wiki</span>
        </Link>

        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          <Link to="/" className={navLinkClass()}>
            Home
          </Link>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={navLinkClass()}
          >
            Spreadsheet
          </a>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors duration-300 hover:text-primary-500 ${
                scrolled ? 'text-foreground-800' : 'text-white/95'
              }`}
            >
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[680px] bg-background-50 rounded-xl p-5 grid grid-cols-2 gap-x-6 gap-y-1 shadow-xl border border-background-200/70">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 group whitespace-nowrap"
                  >
                    <span className="w-8 h-8 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shopping-bag-line text-primary-500 text-sm"></i>
                    </span>
                    <span className="font-medium">{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-xs text-foreground-400 group-hover:text-primary-500 ml-auto"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/pandabuy-spreadsheet-guide-2026"
            className={navLinkClass()}
          >
            Shopping Guide
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap border transition-all duration-300 ${
              scrolled
                ? 'border-foreground-300 text-foreground-700 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950'
                : 'border-white/50 text-white hover:bg-white/15'
            }`}
          >
            Login
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            scrolled ? 'text-foreground-950' : 'text-white'
          }`}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
            >
              <i className="ri-home-4-line text-primary-500"></i>
              <span className="font-medium text-sm">Home</span>
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
            >
              <i className="ri-table-line text-primary-500"></i>
              <span className="font-medium text-sm">Spreadsheet</span>
            </a>
            <Link
              to="/pandabuy-spreadsheet-guide-2026"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
            >
              <i className="ri-book-open-line text-primary-500"></i>
              <span className="font-medium text-sm">Shopping Guide</span>
            </Link>

            <div className="pt-2 pb-1">
              <p className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                Categories
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-shopping-bag-line text-primary-400 text-xs"></i>
                    <span className="text-xs">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t border-background-200/50">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="block w-full text-center py-3 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap"
              >
                Go to Pandabuy Sheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}